/**
 * voiceFillerEngine.ts
 * Natural voice filler engine for AI Avatar during background knowledge & whitelist searches.
 * Eliminates dead air/silence and drives Avatar gesture/lipsync while tools run.
 */

export type VoiceFillerType = 'knowledge_init' | 'whitelist_web' | 'custom';

const KNOWLEDGE_INIT_PHRASES = [
    "Dạ, để em kiểm tra thông tin trong tài liệu cho mình nhé ạ.",
    "Dạ, em đang tra cứu dữ liệu dự án cho anh chị đây ạ.",
    "Dạ để em đối chiếu thông tin chính thức của ban quản lý cho mình nhé ạ."
];

let currentUtterance: SpeechSynthesisUtterance | null = null;
let isFillerActive = false;
let lastSpokenText = "";
let lastSpokenTime = 0;

function getRandomPhrase(phrases: string[]): string {
    const idx = Math.floor(Math.random() * phrases.length);
    return phrases[idx];
}

function dispatchSpeakingEvent(speaking: boolean, text: string = "") {
    if (typeof window === 'undefined') return;
    try {
        window.dispatchEvent(new CustomEvent('sg3d:voice-filler-speaking', {
            detail: { isSpeaking: speaking, text }
        }));
    } catch (_) {}
}

/**
 * Natural voice filler engine.
 * External MP3 playback is disabled per user directive to ensure 100% voice fidelity
 * with the active AI Agent persona/model (Gemini Live / OpenAI Realtime).
 * Visual checkpoints and status events continue to drive HUD indicators.
 */
export function speakNaturalFiller(type: VoiceFillerType, domainOrText?: string): string | null {
    if (typeof window === 'undefined') return null;

    const now = Date.now();
    if (now - lastSpokenTime < 5000) {
        return null;
    }

    let phraseToRecord = "";
    if (type === 'knowledge_init') {
        phraseToRecord = getRandomPhrase(KNOWLEDGE_INIT_PHRASES);
    } else if (type === 'whitelist_web') {
        const cleanDomain = domainOrText ? domainOrText.replace(/^https?:\/\//i, '').replace(/\/.*$/, '') : '';
        phraseToRecord = cleanDomain
            ? `Dạ, em đang tra cứu thêm trên cổng thông tin chính thức ${cleanDomain} cho mình nhé ạ.`
            : "Dạ, em đang kết nối tới cổng thông tin chính thức để tra cứu thêm cho anh chị ạ.";
    } else {
        phraseToRecord = domainOrText || "Dạ để em kiểm tra thông tin nhé ạ.";
    }

    lastSpokenText = phraseToRecord;
    lastSpokenTime = now;

    // Visual & HUD status notification without conflicting external MP3 playback
    dispatchSpeakingEvent(false, phraseToRecord);
    return phraseToRecord;
}

export function cancelNaturalFiller(): void {
    if (typeof window === 'undefined') return;
    try {
        if (window.speechSynthesis && (isFillerActive || window.speechSynthesis.speaking)) {
            if (currentUtterance) {
                currentUtterance.volume = 0;
            }
            window.speechSynthesis.cancel();
        }
        isFillerActive = false;
        currentUtterance = null;
        dispatchSpeakingEvent(false);
    } catch (_) {}
}

export function isNaturalFillerSpeaking(): boolean {
    return isFillerActive;
}

export function getLastSpokenFiller(): string {
    return lastSpokenText;
}

export function resetVoiceFillerEngineForTesting(): void {
    lastSpokenTime = 0;
    isFillerActive = false;
    currentUtterance = null;
    lastSpokenText = "";
}

const voiceFillerEngine = {
    speak: speakNaturalFiller,
    cancel: cancelNaturalFiller,
    isSpeaking: isNaturalFillerSpeaking,
    getLastSpoken: getLastSpokenFiller,
    resetForTesting: resetVoiceFillerEngineForTesting
};

export default voiceFillerEngine;
